import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { ProjectContact, ProjectContactLink, ProjectContactsService } from '../project-contacts.service';

@Component({
  selector: 'app-project-contacts-manager',
  templateUrl: './project-contacts-manager.component.html',
  styleUrls: ['./project-contacts-manager.component.scss'],
  standalone: false
})
export class ProjectContactsManagerComponent implements OnChanges {
  @Input() visible = false;
  @Input() projectId: number | null = null;
  @Input() organizationId: number | null = null;
  @Input() projectName = '';
  @Input() organizationName = '';
  @Input() readOnly = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<number>();

  totalContacts = 0;
  rows: ProjectContact[] = [];
  loading = false;
  saving = false;
  errorMessage = '';
  search = '';
  copiedId: number | null = null;
  loadFailed = false;
  showDiscardConfirm = false;

  private contacts = new Map<number, ProjectContact>();
  private savedLinks = new Map<number, boolean>();
  staged = new Map<number, boolean>();
  private autoPrimaryId: number | null = null;

  constructor(private readonly projectContactsService: ProjectContactsService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue && this.projectId && this.organizationId) {
      this.reset();
      this.load();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible && !this.saving && !this.showDiscardConfirm) this.requestClose();
  }

  get locked(): boolean {
    return this.readOnly || this.loadFailed;
  }

  private reset(): void {
    this.search = '';
    this.errorMessage = '';
    this.loadFailed = false;
    this.showDiscardConfirm = false;
  }

  private load(): void {
    this.loading = true;
    forkJoin([
      this.projectContactsService.getOrganizationContacts(this.organizationId!),
      this.projectContactsService.getProjectContacts(this.projectId!)
    ]).pipe(finalize(() => this.loading = false)).subscribe(([organization, project]) => {
      this.loadFailed = project === null || organization === null;
      if (this.loadFailed) this.errorMessage = 'Contacts could not be loaded. Close and try again.';
      this.contacts = new Map((organization || []).map(c => [c.contactId, c]));
      (project || []).forEach(c => this.contacts.set(c.contactId, { ...this.contacts.get(c.contactId), ...c }));
      this.savedLinks = new Map((project || []).map(c => [c.contactId, c.isPrimary]));
      this.staged = new Map(this.savedLinks);
      this.autoPrimaryId = null;
      this.refresh();
    });
  }

  get primaryContact(): ProjectContact | null {
    const id = [...this.staged].find(([, isPrimary]) => isPrimary)?.[0];
    return id ? this.contacts.get(id) || null : null;
  }

  private refresh(): void {
    const term = this.search.trim().toLowerCase();
    const onProject = new Set([...this.staged.keys(), ...this.savedLinks.keys()]);
    const pool = [...this.contacts.values()].filter(c => this.locked ? onProject.has(c.contactId) : (c.isActive || onProject.has(c.contactId)));
    this.totalContacts = pool.length;
    this.rows = pool
      .filter(c => !term || c.name.toLowerCase().includes(term) || (c.email || '').toLowerCase().includes(term))
      .sort((a, b) =>
        Number(this.savedLinks.has(b.contactId)) - Number(this.savedLinks.has(a.contactId))
        || Number(this.savedLinks.get(b.contactId) === true) - Number(this.savedLinks.get(a.contactId) === true)
        || a.name.localeCompare(b.name));
  }

  toggle(contact: ProjectContact): void {
    if (this.locked) return;
    if (this.staged.has(contact.contactId)) this.remove(contact);
    else if (this.savedLinks.has(contact.contactId)) this.undo(contact);
    else this.add(contact);
  }

  initials(contact: ProjectContact): string {
    const parts = contact.name.split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  get pending(): { add: number[]; remove: number[]; upsert: ProjectContactLink[]; primaryChanged: boolean; total: number } {
    const add = [...this.staged.keys()].filter(id => !this.savedLinks.has(id));
    const remove = [...this.savedLinks.keys()].filter(id => !this.staged.has(id));
    const upsert = [...this.staged]
      .filter(([id, isPrimary]) => !this.savedLinks.has(id) || this.savedLinks.get(id) !== isPrimary)
      .map(([contactId, isPrimary]) => ({ contactId, isPrimary }));
    const before = [...this.savedLinks].find(([, p]) => p)?.[0];
    const after = [...this.staged].find(([, p]) => p)?.[0];
    const primaryChanged = before !== after;
    return { add, remove, upsert, primaryChanged, total: add.length + remove.length + (primaryChanged ? 1 : 0) };
  }

  get needsPrimary(): boolean {
    return this.staged.size > 0 && !this.primaryContact;
  }

  get summary(): string {
    const p = this.pending, parts: string[] = [];
    if (p.add.length) parts.push(`${p.add.length} to add`);
    if (p.remove.length) parts.push(`${p.remove.length} to remove`);
    if (p.primaryChanged && this.primaryContact) parts.push('primary changed');
    return parts.join(' · ');
  }

  status(contact: ProjectContact): 'new' | 'removing' | 'on' | 'available' {
    const on = this.staged.has(contact.contactId), was = this.savedLinks.has(contact.contactId);
    if (was && !on) return 'removing';
    if (on && !was) return 'new';
    return on ? 'on' : 'available';
  }

  isPrimary(contact: ProjectContact): boolean {
    return this.staged.get(contact.contactId) === true;
  }

  onSearch(term: string): void {
    this.search = term;
    this.refresh();
  }

  add(contact: ProjectContact): void {
    if (!contact) return;
    const first = this.staged.size === 0;
    this.staged.set(contact.contactId, first);
    if (first) this.autoPrimaryId = contact.contactId;
    this.refresh();
  }

  remove(contact: ProjectContact): void {
    const wasPrimary = this.staged.get(contact.contactId);
    this.staged.delete(contact.contactId);
    if (wasPrimary) this.promoteOnlyContact();
    this.refresh();
  }

  undo(contact: ProjectContact): void {
    const id = contact.contactId;
    if (!this.savedLinks.has(id)) {
      this.remove(contact);
      return;
    }
    const current = this.primaryContact?.contactId;
    const reclaim = this.savedLinks.get(id) === true && (current === undefined || current === this.autoPrimaryId);
    if (reclaim) {
      for (const key of this.staged.keys()) this.staged.set(key, false);
      this.staged.set(id, true);
      this.autoPrimaryId = null;
    } else if (this.staged.size === 0) {
      this.staged.set(id, true);
      this.autoPrimaryId = id;
    } else {
      this.staged.set(id, false);
    }
    this.refresh();
  }

  setPrimary(contact: ProjectContact): void {
    if (this.locked) return;
    for (const id of this.staged.keys()) this.staged.set(id, id === contact.contactId);
    this.autoPrimaryId = null;
    this.refresh();
  }

  private promoteOnlyContact(): void {
    if (this.staged.size === 1) {
      const [only] = this.staged.keys();
      this.staged.set(only, true);
      this.autoPrimaryId = only;
    }
  }

  copyEmail(contact: ProjectContact): void {
    if (!contact.email) return;
    Promise.resolve().then(() => navigator.clipboard.writeText(contact.email!)).then(() => {
      this.copiedId = contact.contactId;
      setTimeout(() => { if (this.copiedId === contact.contactId) this.copiedId = null; }, 2000);
    }, () => { });
  }

  save(): void {
    const { remove, upsert } = this.pending;
    if (this.locked || this.saving || this.needsPrimary || (!remove.length && !upsert.length)) return;
    this.saving = true;
    this.errorMessage = '';
    const projectId = this.projectId!;
    const upsert$: Observable<any> = upsert.length ? this.projectContactsService.associateContacts(projectId, upsert) : of(null);
    upsert$.pipe(
      switchMap(() => remove.length ? this.projectContactsService.removeContacts(projectId, remove) : of(null)),
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => {
        this.savedLinks = new Map(this.staged);
        this.saved.emit(projectId);
        this.close();
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.message || 'Contacts could not be saved. Please try again.';
        this.saved.emit(projectId);
        this.load();
      }
    });
  }

  requestClose(): void {
    if (!this.pending.total) {
      this.close();
      return;
    }
    this.showDiscardConfirm = true;
  }

  onDiscardConfirm(answer: string): void {
    this.showDiscardConfirm = false;
    if (answer === 'Yes') this.close();
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
